import React from 'react';
import '../../css/PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
      <div className="privacypolicy-container">
          <h1>Privacy Notice</h1>
          <p><strong>Last updated:</strong> November 28, 2024</p>
          <p>
              This Privacy Notice for Mentium Inc. ("we," "us," or "our"), describes how and why we might access,
              collect, store, use, and/or share ("process") your personal information when you use our services
              ("Services"), including when you:
          </p>
          <p><strong>Questions or concerns?</strong></p>
          <p>
              Reading this Privacy Notice will help you understand your privacy rights and choices. We are responsible
              for making decisions about how your personal information is processed. If you do not agree with our
              policies and practices, please do not use our Services.
          </p>

          <h2>What personal information do we process?</h2>
          <p>
              When you visit, use, or navigate our Services, we may process personal information depending on how you
              interact with us and the Services, the choices you make, and the products and features you use. <a
              href="/">Learn more about the personal information you disclose to us.</a>
          </p>

          <h2>Do we process any sensitive personal information?</h2>
          <p>
              Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example,
              your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive
              personal information.
          </p>

          <h2>Do we collect any information from third parties?</h2>
          <p>
              We may collect information from public databases, marketing partners, social media platforms, and other
              outside sources. <a href="/">Learn more about information collected from other sources.</a>
          </p>

          <h2>How do we process your information?</h2>
          <p>
              We process your information to provide, improve, and administer our Services, communicate with you for
              security and fraud prevention, and comply with the law. We may also process your information for other
              purposes with your consent. <a href="/">Learn more about how we process your information.</a>
          </p>

          <h2>In what situations and with which parties do we share personal information?</h2>
          <p>
              We may share information in specific situations and with specific third parties. <a href="/">Learn more
              about when and with whom we share your personal information.</a>
          </p>

          <h2>What are your rights?</h2>
          <p>
              Depending on where you are located geographically, the applicable privacy law may mean you have certain
              rights regarding your personal information. <a href="/">Learn more about your privacy rights.</a>
          </p>

          <h2>How do you exercise your rights?</h2>
          <p>
              The easiest way to exercise your rights is by submitting a data subject access request or contacting us.
              We will consider and act upon any request in accordance with applicable data protection laws.
          </p>

          <h2>1. What Information Do We Collect?</h2>
          <p><strong>Personal information you disclose to us</strong></p>
          <p>
              We collect personal information that you voluntarily provide to us when you register on the Services,
              express an interest in obtaining information about us or our products and Services, participate in
              activities on the Services, or otherwise when you contact us.
          </p>
          <p><strong>Sensitive Information:</strong> We do not process sensitive information.</p>
          <p>
              All personal information that you provide to us must be true, complete, and accurate, and you must notify
              us of any changes to such personal information.
          </p>

          <p><strong>Information automatically collected</strong></p>
          <p>
              We automatically collect certain information when you visit, use, or navigate the Services. This
              information does not reveal your specific identity (like your name or contact information) but may include
              device and usage information, such as your IP address, browser and device characteristics, operating
              system, language preferences, referring URLs, device name, country, location, information about how and
              when you use our Services, and other technical information. This information is primarily needed to
              maintain the security and operation of our Services, and for our internal analytics and reporting
              purposes.
          </p>
          <p>Like many businesses, we also collect information through cookies and similar technologies.</p>

          <h2>2. How Do We Process Your Information?</h2>
          <p>
              We process your information to provide, improve, and administer our Services, communicate with you for
              security and fraud prevention, and comply with the law. We may also process your information for other
              purposes with your consent. We process your personal information for various reasons, depending on how you
              interact with our Services.
          </p>

          <h2>3. When and With Whom Do We Share Your Personal Information?</h2>
          <p>
              We may need to share your personal information in the following situations:
          </p>
          <ul>
              <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with or
                  during negotiations of any merger, sale of company assets, financing, or acquisition of all or a
                  portion of our business to another company.
              </li>
              <li><strong>Affiliates:</strong> We may share your information with our affiliates, in which case we will
                  require those affiliates to honor this Privacy Notice.
              </li>
              <li><strong>Business Partners:</strong> We may share your information with our business partners to offer
                  you certain products, services, or promotions.
              </li>
          </ul>

          <h2>4. Do We Use Cookies and Other Tracking Technologies?</h2>
          <p>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information
              when you interact with our Services. Some online tracking technologies help us maintain the security of
              our Services, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.
          </p>
          <p>
              Our <a href="/">Cookie Notice</a> provides specific information about how we use such technologies and how
              you can refuse certain cookies.
          </p>

          <h2>5. How Do We Handle Your Social Logins?</h2>
          <p>
              Our Services offer you the ability to register and log in using your third-party social media account
              details. Where you choose to do this, we will receive certain profile information about you from your
              social media provider.
          </p>
          <p>
              We will use the information we receive only for the purposes described in this Privacy Notice. <a
              href="/">Learn more about how we handle your social logins.</a>
          </p>

          <h2>6. Is Your Information Transferred Internationally?</h2>
          <p>
              Our servers are located in the US. If you are accessing our Services from outside, please be aware that
              your information may be transferred to, stored by, and processed by us in our facilities and the
              facilities of third parties.
          </p>

          <h2>7. How Long Do We Keep Your Information?</h2>
          <p>
              We only keep your personal information as long as necessary for the purposes set out in this Privacy
              Notice unless a longer retention period is required or permitted by law.
          </p>

          <h2>8. Do We Collect Information From Minors?</h2>
          <p>
              We do not knowingly collect, solicit data from, or market to children under 18 years of age. <a href="/">Learn
              more about our policy for minors.</a>
          </p>

          <h2>9. What Are Your Privacy Rights?</h2>
          <p>
              You may review, change, or terminate your account at any time, depending on your geographic location and
              applicable laws. <a href="/">Learn more about your privacy rights.</a>
          </p>

          <h2>10. Controls for Do-Not-Track Features</h2>
          <p>
              Most web browsers include a Do-Not-Track ("DNT") feature or setting. <a href="/">Learn more about our DNT
              policies.</a>
          </p>

          <h2>11. Do We Make Updates to This Notice?</h2>
          <p>
              Yes, we will update this notice as necessary to stay compliant with relevant laws. <a href="/">Learn more
              about updates.</a>
          </p>

          <h2>12. How Can You Contact Us About This Notice?</h2>
          <p>
              If you have questions or comments, contact us at <a href="mailto:info@mentium.pro">info@mentium.pro</a>.
          </p>
      </div>
  );
}

export default PrivacyPolicy;
